// apps/spa/src/hooks/useBotHealth.ts
// Polls GET /api/bot-health every 60s while the tab is visible. Pauses when
// document.visibilityState transitions to 'hidden' — RESEARCH Anti-Pattern guard.
//
// On window focus, refetches eagerly (refetchOnWindowFocus: true) so an officer
// returning to the tab sees fresh state without waiting for the next poll cycle.
//
// Returns the raw query data + isLoading. Components format the lastChecked delta.

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type BotHealth = {
  online: boolean;
  lastChecked: number;
};

export function useBotHealth(): {
  data: BotHealth | undefined;
  isLoading: boolean;
} {
  const [tabVisible, setTabVisible] = useState<boolean>(() => {
    if (typeof document === 'undefined') return true;
    return document.visibilityState !== 'hidden';
  });

  useEffect(() => {
    const handler = () => setTabVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  const q = useQuery<BotHealth>({
    queryKey: ['bot-health'],
    queryFn: () => api<BotHealth>('/api/bot-health'),
    refetchInterval: tabVisible ? 60_000 : false,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    retry: false,
  });

  return { data: q.data, isLoading: q.isLoading };
}
